import React, {
    useEffect,
    useState,
} from "react";

import {
    Box,
    Text,
    Badge,
    Select,
    Stack,
    Flex,
} from "@chakra-ui/react";

import { useDispatch, useSelector } from "react-redux";

import {
    updateDriverPartnerStatusAction,
    fetchDriverPartnersAction,
} from "state/actions/driverPartnerActions";

import { useCustomNotification } from "components/Toasts/OpenNotification";

import ReusableDrawer from "components/Drawers/ReusableDrawer";
import GradientButton from "components/Buttons/GradientButton";
import ReusableInput from "components/Inputs/ReusableInput";
import StatusLogs from "components/Card/StatusLogs";

import headerImage from "assets/img/background.png";

import { fetchStatusHistoryAction } from "state/actions/statusHistoryActions";

const EditDriverPartnerDrawer = ({
    partner,
    isOpen,
    onClose,
}) => {
    const dispatch = useDispatch();

    const notify =
        useCustomNotification();

    const [
        status,
        setStatus,
    ] = useState("");

    const [
        reason,
        setReason,
    ] = useState("");

    const {
        history,
        loading,
    } = useSelector(
        (state) =>
            state.statusHistory
    );

    useEffect(() => {
        setStatus(
            partner
                ?.driver_partner
                ?.status || ""
        );
    }, [partner]);

    useEffect(() => {
        if (
            partner?.driver_partner
                ?.driver_partner_id
        ) {
            dispatch(
                fetchStatusHistoryAction(
                    "driver_partner",
                    partner
                        .driver_partner
                        .driver_partner_id
                )
            );
        }
    }, [
        dispatch,
        partner,
    ]);

    const handleSave =
        async () => {
            const response =
                await dispatch(
                    updateDriverPartnerStatusAction(
                        partner
                            ?.driver_partner
                            ?.driver_partner_id,
                        status,
                        reason
                    )
                );

            if (
                response.success
            ) {
                notify({
                    type: "success",
                    message:
                        "Driver Partner Updated",
                    description:
                        "Status updated successfully.",
                });

                dispatch(
                    fetchDriverPartnersAction()
                );

                onClose();
            } else {
                notify({
                    type: "error",
                    message:
                        "Update Failed",
                    description:
                        response.error,
                });
            }
        };

    const badgeColor = {
        Approved:
            "green",
        Pending:
            "yellow",
        Suspended:
            "red",
        Rejected:
            "orange",
        Deactivated:
            "gray",
    };

    return (
        <ReusableDrawer
            isOpen={isOpen}
            onClose={onClose}
            headerImage={
                headerImage
            }
            headerContent={
                <h2>
                    Edit Driver
                    Partner
                </h2>
            }
            bodyContent={
                <Box>
                    <Text
                        fontWeight="500"
                        fontSize="sm"
                        color="#8D93A1"
                        mb={1}
                    >
                        Current Status
                    </Text>

                    <Badge
                        colorScheme={
                            badgeColor[
                                status
                            ] ||
                            "gray"
                        }
                        borderRadius="full"
                        px={3}
                        py={1}
                        mb={5}
                    >
                        {status}
                    </Badge>

                    <Text
                        fontWeight="500"
                        fontSize="sm"
                        color="#8D93A1"
                        mb={1}
                    >
                        Update Status
                    </Text>

                    <Select
                        mb={5}
                        value={
                            status
                        }
                        onChange={(
                            e
                        ) =>
                            setStatus(
                                e
                                    .target
                                    .value
                            )
                        }
                    >
                        <option value="Pending">
                            Pending
                        </option>

                        <option value="Approved">
                            Approved
                        </option>

                        <option value="Rejected">
                            Rejected
                        </option>

                        <option value="Suspended">
                            Suspended
                        </option>

                        <option value="Deactivated">
                            Deactivated
                        </option>
                    </Select>

                    <ReusableInput
                        label="Reason for Status Change"
                        placeholder="Provide a reason..."
                        value={
                            reason
                        }
                        onChange={(
                            e
                        ) =>
                            setReason(
                                e
                                    .target
                                    .value
                            )
                        }
                        type="textarea"
                    />

                    <Box mt={8}>
                        <Text
                            fontWeight="600"
                            fontSize="sm"
                            color="#8D93A1"
                            mb={4}
                        >
                            Status
                            History
                        </Text>
                        {loading ? (
                            <Flex
                                overflowX="auto"
                                gap={4}
                            >
                                {[1, 2, 3].map(
                                    (
                                        item
                                    ) => (
                                        <Box
                                            key={
                                                item
                                            }
                                            minW="260px"
                                            p={4}
                                            borderRadius="md"
                                            bg="gray.50"
                                        >
                                            <Box
                                                h="20px"
                                                w="90px"
                                                bg="gray.200"
                                                borderRadius="md"
                                                mb={3}
                                            />

                                            <Box
                                                h="10px"
                                                w="70%"
                                                bg="gray.200"
                                                mb={2}
                                            />

                                            <Box
                                                h="10px"
                                                w="90%"
                                                bg="gray.200"
                                                mb={2}
                                            />

                                            <Box
                                                h="10px"
                                                w="60%"
                                                bg="gray.200"
                                                mb={2}
                                            />

                                            <Box
                                                h="10px"
                                                w="50%"
                                                bg="gray.200"
                                                mb={3}
                                            />

                                            <Flex justify="flex-end">
                                                <Box
                                                    h="30px"
                                                    w="30px"
                                                    borderRadius="full"
                                                    bg="gray.200"
                                                />
                                            </Flex>
                                        </Box>
                                    )
                                )}
                            </Flex>
                        ) : (
                            <StatusLogs
                                logs={
                                    history
                                }
                            />
                        )}
                    </Box>
                </Box>
            }
            footerContent={
                <Stack
                    direction="row"
                    spacing={4}
                    justifyContent="flex-end"
                >
                    <GradientButton
                        onClick={
                            onClose
                        }
                        width="110px"
                        height="42px"
                    >
                        Cancel
                    </GradientButton>

                    <GradientButton
                        onClick={
                            handleSave
                        }
                        width="110px"
                        height="42px"
                    >
                        Save
                    </GradientButton>
                </Stack>
            }
        />
    );
};

export default EditDriverPartnerDrawer;